import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import createDatafeed, { setLegendResolution } from "./tvDatafeed";
import { setChartWidget, chartStyleFromPref } from "./chartToolBridge";
import {
  CHART_PREF_EVENT,
  getColorTemplateOverrides,
  loadChartPreferences,
} from "utils/chartPreferences";

function collectRows(m) {
  if (!m) return [];
  if (m instanceof Map) return Array.from(m.values());
  if (Array.isArray(m)) return m;
  if (typeof m === "object") return Object.values(m);
  return [];
}

function getSymbolFromProps(symbol, selectedRow) {
  const s = String(symbol ?? "").trim();
  if (s) return s;
  return String(
    selectedRow?.script ?? selectedRow?.displayName ?? selectedRow?.symbol ?? selectedRow?.name ?? ""
  ).trim();
}

function applyChartSymbol(w, next, force = false) {
  if (!w || !next) return;
  try {
    const chart = w.activeChart?.() || w.chart?.();
    if (!chart?.setSymbol) return;
    const cur = String(chart.symbol?.() ?? "").trim();
    if (!force && cur.toUpperCase() === String(next).trim().toUpperCase()) return;
    chart.setSymbol(next);
  } catch {}
}

const STRIP_CSS = `
  #drawing-toolbar,
  [data-name="toolbar-drawing-toggle-button"] {
    display: none !important;
  }
  *:has(> #drawing-toolbar) {
    width: 0 !important;
    min-width: 0 !important;
    max-width: 0 !important;
    flex: 0 0 0px !important;
    overflow: hidden !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    background: transparent !important;
  }
  .chart-gui-wrapper {
    padding-inline-start: 0 !important;
  }
  [data-name="linetool-properties-toolbar"],
  [data-name="chart-floating-toolbar"],
  .tv-floating-toolbar,
  div[class*="drawingToolbarFloating"] {
    display: none !important;
  }
  /* Loading spinner / leftover 5px toolbar column — never paint the grey flash. */
  .tv-spinner,
  [class*="tv-spinner"],
  [class*="loadingScreen"],
  [class*="loaderWrap"] {
    display: none !important;
    visibility: hidden !important;
  }
  /* Force time/date axis strip to stay visible (mobile short viewports). */
  .chart-markup-table > tr:last-child,
  .chart-markup-table > tbody > tr:last-child {
    min-height: 28px !important;
  }
  .time-axis {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    min-height: 28px !important;
    overflow: visible !important;
    pointer-events: auto !important;
  }
  .pane {
    overflow: hidden !important;
  }
  [class*="paneControls"],
  [class*="legendButtons"],
  [class*="buttonsWrapper"],
  [data-name="legend"] button,
  [data-name="legend-more-button"],
  [data-name="legend-settings-action"] {
    display: none !important;
  }
  .price-axis,
  .time-axis,
  .price-axis-container {
    overflow: hidden !important;
    position: relative !important;
  }
  .fx-mt5-price-ticks,
  .fx-mt5-time-ticks,
  .fx-mt5-plot-frame {
    position: absolute !important;
    inset: 0 !important;
    pointer-events: none !important;
    z-index: 30 !important;
  }
  .fx-mt5-plot-frame {
    z-index: 25 !important;
  }
  /* TradingView first-run hints: "Press and hold…" / "Got it!" / zoom shortcuts. */
  [data-role="hint"],
  [class*="hintReference"],
  #overlap-manager-root [data-role="hint"] {
    display: none !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }
`;

function ensureTimeAxisVisible(hostEl) {
  const doc = hostEl?.querySelector?.("iframe")?.contentDocument;
  if (!doc?.body) return false;
  const rows = doc.querySelectorAll(
    ".chart-markup-table > tr:last-child, .chart-markup-table > tbody > tr:last-child"
  );
  rows.forEach((row) => {
    row.style.setProperty("min-height", "28px", "important");
  });
  const axes = doc.querySelectorAll(".time-axis");
  if (!axes.length) return false;
  axes.forEach((node) => {
    node.style.setProperty("display", "block", "important");
    node.style.setProperty("visibility", "visible", "important");
    node.style.setProperty("opacity", "1", "important");
    node.style.setProperty("min-height", "28px", "important");
    node.style.setProperty("overflow", "visible", "important");
  });
  return true;
}

/** Clip plot so grid/lines stop at the corner; L-frame only on the pane (not axes). */
function ensurePaneGridClip(hostEl, darkMode) {
  const doc = hostEl?.querySelector?.("iframe")?.contentDocument;
  if (!doc?.body) return false;

  const frame = darkMode ? "#787b86" : "#131722";

  const panes = Array.from(doc.querySelectorAll(".pane")).filter(
    (pane) => pane.clientWidth > 8 && pane.clientHeight > 8
  );
  panes.forEach((pane, idx) => {
    pane.style.setProperty("overflow", "hidden", "important");
    pane.style.setProperty("position", "relative", "important");
    // Clear CSS box-shadow frame — drawn by ensurePlotFrame canvas instead
    pane.style.removeProperty("box-shadow");

    let p = pane.parentElement;
    for (let i = 0; i < 6 && p; i += 1) {
      if (p.tagName === "TD") {
        p.style.setProperty("overflow", "hidden", "important");
        break;
      }
      p = p.parentElement;
    }

    // Visible L on plot only (stops at corner — never enters axis label cells)
    let frameLayer = pane.querySelector(":scope > .fx-mt5-plot-frame");
    if (!frameLayer) {
      frameLayer = doc.createElement("canvas");
      frameLayer.className = "fx-mt5-plot-frame";
      frameLayer.setAttribute("aria-hidden", "true");
      Object.assign(frameLayer.style, {
        position: "absolute",
        inset: "0",
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: "25",
      });
      pane.appendChild(frameLayer);
    }
    const dpr = Math.max(1, doc.defaultView?.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(pane.clientWidth));
    const h = Math.max(1, Math.round(pane.clientHeight));
    const bw = Math.round(w * dpr);
    const bh = Math.round(h * dpr);
    if (frameLayer.width !== bw || frameLayer.height !== bh) {
      frameLayer.width = bw;
      frameLayer.height = bh;
    }
    frameLayer.style.width = `${w}px`;
    frameLayer.style.height = `${h}px`;
    const ctx = frameLayer.getContext("2d");
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = frame;
      ctx.lineWidth = 1;
      ctx.lineJoin = "miter";
      ctx.miterLimit = 4;
      ctx.beginPath();
      // One connected L on the last pane so vertical + horizontal meet.
      const x = w - 1.5;
      const y = h - 1.5;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, y);
      if (idx === panes.length - 1) {
        ctx.lineTo(0, y);
      }
      ctx.stroke();
    }
  });

  // No borders on axis/corner cells — those caused the "+" overshoot past the corner
  doc.querySelectorAll(".price-axis, .time-axis, .price-axis-container").forEach((node) => {
    node.style.setProperty("overflow", "hidden", "important");
    node.style.setProperty("border", "none", "important");
    node.style.removeProperty("box-shadow");
  });

  const corner = doc.querySelector(
    ".chart-markup-table > tr:last-child > td:last-child, .chart-markup-table > tbody > tr:last-child > td:last-child"
  );
  if (corner) {
    corner.style.setProperty("border", "none", "important");
    corner.style.removeProperty("box-shadow");
  }

  // Clip plot canvases that spill into axis cells
  const priceAxes = Array.from(
    doc.querySelectorAll(".price-axis, .price-axis-container")
  );
  const timeAxes = Array.from(doc.querySelectorAll(".time-axis"));

  doc.querySelectorAll("canvas").forEach((canvas) => {
    if (
      canvas.closest(
        ".price-axis, .time-axis, .price-axis-container, .fx-mt5-price-ticks, .fx-mt5-time-ticks, .fx-mt5-plot-frame"
      )
    ) {
      canvas.style.removeProperty("clip-path");
      return;
    }
    const pane = canvas.closest(".pane");
    if (pane) {
      const paneCanvases = Array.from(pane.querySelectorAll(":scope > canvas, :scope canvas")).filter(
        (c) =>
          !c.classList.contains("fx-mt5-plot-frame") &&
          !c.closest(".fx-mt5-price-ticks, .fx-mt5-time-ticks")
      );
      // Drawing tools sit on later overlay canvases — never clip those.
      if (paneCanvases.indexOf(canvas) >= 2) {
        canvas.style.removeProperty("clip-path");
        return;
      }
    }
    const cRect = canvas.getBoundingClientRect();
    if (cRect.width < 8 || cRect.height < 8) return;

    let clipRight = 0;
    let clipBottom = 0;

    priceAxes.forEach((ax) => {
      const a = ax.getBoundingClientRect();
      if (a.width < 1 || a.height < 1) return;
      const overlapsY = cRect.bottom > a.top + 1 && cRect.top < a.bottom - 1;
      const spill = cRect.right - a.left;
      if (overlapsY && spill > 0.5 && cRect.left < a.left - 0.5) {
        clipRight = Math.max(clipRight, spill);
      }
    });

    timeAxes.forEach((ax) => {
      const a = ax.getBoundingClientRect();
      if (a.width < 1 || a.height < 1) return;
      const overlapsX = cRect.right > a.left + 1 && cRect.left < a.right - 1;
      const spill = cRect.bottom - a.top;
      if (overlapsX && spill > 0.5 && cRect.top < a.top - 0.5) {
        clipBottom = Math.max(clipBottom, spill);
      }
    });

    // Axis spill is a few pixels. Large insets (common while zooming, when
    // canvas/axis rects are mid-layout) cut drawings off the plot.
    const maxRight = Math.min(12, cRect.width * 0.08);
    const maxBottom = Math.min(12, cRect.height * 0.08);
    clipRight = Math.min(clipRight, maxRight);
    clipBottom = Math.min(clipBottom, maxBottom);

    if (clipRight > 0.5 || clipBottom > 0.5) {
      canvas.style.setProperty(
        "clip-path",
        `inset(0 ${Math.ceil(clipRight)}px ${Math.ceil(clipBottom)}px 0)`,
        "important"
      );
    } else {
      canvas.style.removeProperty("clip-path");
    }
  });

  return true;
}

/** Inject strip-hiding CSS as soon as the iframe document exists (before chart-ready). */
function injectStripCssEarly(hostEl) {
  const iframe = hostEl?.querySelector?.("iframe");
  const doc = iframe?.contentDocument;
  if (!doc?.head) return false;
  let style = doc.getElementById("fx-remove-toolbar-strip");
  if (!style) {
    style = doc.createElement("style");
    style.id = "fx-remove-toolbar-strip";
    doc.head.appendChild(style);
  }
  style.textContent = STRIP_CSS;
  return true;
}

function hideChartPopupHints(hostEl) {
  const doc = hostEl?.querySelector?.("iframe")?.contentDocument;
  if (!doc?.body) return;
  doc.querySelectorAll('[data-role="hint"], [class*="hintReference"]').forEach((node) => {
    node.style.setProperty("display", "none", "important");
    node.style.setProperty("visibility", "hidden", "important");
    node.style.setProperty("pointer-events", "none", "important");
  });
}

/**
 * Remove the native left-toolbar column completely (not leave a 5px collapse).
 * ChartToolRail replaces that UI; selectLineTool still needs the toolbar mounted.
 */
function removeNativeToolbarStrip(hostEl) {
  const iframe = hostEl?.querySelector?.("iframe");
  const doc = iframe?.contentDocument;
  if (!doc?.body) return false;

  injectStripCssEarly(hostEl);

  const tb = doc.getElementById("drawing-toolbar");
  if (!tb) return false;

  // Drive layout width WV to 0 (collapsed is still 5px — that leftover strip).
  const bridge = findResizerBridge(tb);
  if (bridge?.negotiateWidth) {
    try { bridge.negotiateWidth(0); } catch {}
  }

  // Find the narrow column the library sizes via style.width (5px or 52px).
  let slot = null;
  for (let node = tb.parentElement; node && node !== doc.body; node = node.parentElement) {
    const rectW = node.getBoundingClientRect().width;
    const styleW = node.style.width;
    const isKnown =
      styleW === "5px" ||
      styleW === "52px" ||
      styleW === "0px" ||
      (rectW > 0 && rectW <= 56);
    if (!isKnown) break;
    const parent = node.parentElement;
    const parentW = parent?.getBoundingClientRect().width ?? 0;
    // Stop before the wide layout row that also holds the chart.
    if (parentW > 120 && parentW > rectW * 2) {
      slot = node;
      break;
    }
    slot = node;
  }
  if (!slot) slot = tb.parentElement;
  if (!slot) return false;

  const kill = () => {
    if (slot.dataset.fxKilling === "1") return;
    slot.dataset.fxKilling = "1";
    try {
      try { bridge?.negotiateWidth?.(0); } catch {}
      slot.style.setProperty("width", "0px", "important");
      slot.style.setProperty("min-width", "0px", "important");
      slot.style.setProperty("max-width", "0px", "important");
      slot.style.setProperty("flex", "0 0 0px", "important");
      slot.style.setProperty("padding", "0", "important");
      slot.style.setProperty("margin", "0", "important");
      slot.style.setProperty("border", "none", "important");
      slot.style.setProperty("overflow", "hidden", "important");
      slot.style.setProperty("background", "transparent", "important");
    } finally {
      slot.dataset.fxKilling = "0";
    }
  };
  kill();

  // Library may write style.width back to 5/52 — keep it at 0.
  if (!slot.dataset.fxStripObs) {
    slot.dataset.fxStripObs = "1";
    const mo = new MutationObserver(kill);
    mo.observe(slot, { attributes: true, attributeFilter: ["style"] });
  }

  return true;
}

/** Walk React fiber from #drawing-toolbar up to resizerBridge.negotiateWidth. */
function findResizerBridge(domNode) {
  const key = Object.keys(domNode).find(
    (k) => k.startsWith("__reactFiber$") || k.startsWith("__reactInternalInstance$")
  );
  if (!key) return null;
  let fiber = domNode[key];
  for (let i = 0; i < 40 && fiber; i += 1) {
    const props = fiber.memoizedProps || fiber.pendingProps;
    if (props?.resizerBridge?.negotiateWidth) return props.resizerBridge;
    fiber = fiber.return;
  }
  return null;
}

/** Chart grid + scale colors. */
function mt5AxisGridOverrides(darkMode, isMobile) {
  const bg = darkMode ? "#131722" : "#ffffff";
  const text = darkMode ? "#D1D4DC" : "#131722";
  const grid = darkMode ? "#2A2E39" : "#ECEFF2";
  // Hide TV native scale borders (they overshoot into a "+" at the corner).
  // Visible L-frame is drawn on the pane in ensurePlotFrame.
  return {
    "paneProperties.background": bg,
    "paneProperties.gridLinesMode": "both",
    "paneProperties.vertGridProperties.color": grid,
    "paneProperties.vertGridProperties.style": 0,
    "paneProperties.horzGridProperties.color": grid,
    "paneProperties.horzGridProperties.style": 0,
    "paneProperties.separatorColor": darkMode ? "#787B86" : "#555555",
    "scalesProperties.fontSize": isMobile ? 11 : 12,
    "scalesProperties.textColor": text,
    "scalesProperties.lineColor": bg,
    "scalesProperties.showSeriesLastValue": true,
    "scalesProperties.showStudyLastValue": false,
    "scalesProperties.showPriceScaleCrosshairLabel": true,
    "scalesProperties.showTimeScaleCrosshairLabel": true,
    "mainSeriesProperties.showPriceLine": true,
    "paneProperties.topMargin": isMobile ? 6 : 10,
    "paneProperties.bottomMargin": isMobile ? 8 : 10,
    // MT5 legend: SYMBOL, TF: SYMBOL expiry + OHLC. Volume/settings stay in the header.
    "paneProperties.legendProperties.showSeriesTitle": true,
    "paneProperties.legendProperties.showSeriesOHLC": true,
    "paneProperties.legendProperties.showBarChange": true,
    "paneProperties.legendProperties.showLastDayChange": false,
    "paneProperties.legendProperties.showStudyTitles": false,
    "paneProperties.legendProperties.showStudyValues": false,
    "paneProperties.legendProperties.showStudyArguments": false,
    "paneProperties.legendProperties.showVolume": false,
    "paneProperties.legendProperties.showTradingButtons": false,
    "paneProperties.legendProperties.showTradingButtonsMobile": false,
    "mainSeriesProperties.statusViewStyle.showInterval": false,
    "mainSeriesProperties.statusViewStyle.showExchange": false,
    "mainSeriesProperties.statusViewStyle.symbolTextSource": "description",
  };
}

/** Labeled axis marks from TV's chart model (only positions that have numbers). */
function readLibraryAxisMarks(widget) {
  try {
    const win =
      (typeof widget?._innerWindow === "function" && widget._innerWindow()) ||
      widget?._iFrame?.contentWindow ||
      null;
    const api =
      (typeof widget?._innerAPI === "function" && widget._innerAPI()) ||
      win?.tradingViewApi ||
      null;

    const coll =
      (typeof api?.chartWidgetCollection === "function" &&
        api.chartWidgetCollection()) ||
      api?._chartWidgetCollection ||
      win?.Zy?.chartWidgetCollection ||
      null;

    const activeWV = coll?.activeChartWidget;
    const cw =
      (typeof activeWV?.value === "function" && activeWV.value()) ||
      activeWV?.value ||
      win?.Zy?.chartWidget ||
      api?.chartWidget ||
      null;

    if (!cw) return { priceMarks: null, timeMarks: null };

    const wm = typeof cw.model === "function" ? cw.model() : cw.model;
    if (!wm) return { priceMarks: null, timeMarks: null };

    // Underlying chart model (has timeScale); widget model has mainSeries
    const cm = typeof wm.model === "function" ? wm.model() : wm;

    let priceMarks = null;
    try {
      priceMarks = wm.mainSeries?.()?.priceScale?.()?.marks?.() ?? null;
    } catch {}
    if (!Array.isArray(priceMarks)) {
      try {
        priceMarks = cm?.mainSeries?.()?.priceScale?.()?.marks?.() ?? null;
      } catch {}
    }
    if (!Array.isArray(priceMarks)) {
      try {
        const pane = (cm || wm)?.panes?.()?.[0];
        priceMarks = pane?.defaultPriceScale?.()?.marks?.() ?? null;
      } catch {}
    }

    let timeMarks = null;
    try {
      timeMarks = (cm || wm)?.timeScale?.()?.marks?.() ?? null;
    } catch {}
    if (!Array.isArray(timeMarks)) {
      try {
        timeMarks = wm.timeScale?.()?.marks?.() ?? null;
      } catch {}
    }

    return {
      priceMarks: Array.isArray(priceMarks) ? priceMarks : null,
      timeMarks: Array.isArray(timeMarks) ? timeMarks : null,
    };
  } catch {
    return { priceMarks: null, timeMarks: null };
  }
}

function ensureAxisTickLayer(parent, className, doc) {
  if (!parent) return null;
  let layer = parent.querySelector(`.${className}`);
  if (!layer) {
    layer = doc.createElement("canvas");
    layer.className = className;
    layer.setAttribute("aria-hidden", "true");
    Object.assign(layer.style, {
      position: "absolute",
      left: "0",
      top: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: "30",
    });
    if (doc.defaultView?.getComputedStyle?.(parent)?.position === "static") {
      parent.style.position = "relative";
    }
    parent.appendChild(layer);
  }
  return layer;
}

function sizeTickCanvas(canvas, el, dpr) {
  const w = Math.max(1, Math.round(el.clientWidth || el.getBoundingClientRect().width));
  const h = Math.max(1, Math.round(el.clientHeight || el.getBoundingClientRect().height));
  const bw = Math.round(w * dpr);
  const bh = Math.round(h * dpr);
  if (canvas.width !== bw || canvas.height !== bh) {
    canvas.width = bw;
    canvas.height = bh;
  }
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  return { w, h };
}

function markHasNumber(m) {
  const label = m?.label ?? m?.text;
  return label != null && String(label).trim() !== "";
}

/**
 * MT5-style ticks OUTSIDE the plot, only beside real axis numbers.
 * Do NOT draw full-length axis borders here — those overshoot into a "+" at the corner.
 * Plot L-frame stays on the pane via ensurePaneGridClip inset shadow.
 */
function ensureMt5AxisTicks(hostEl, widget, darkMode, isMobile) {
  const doc = hostEl?.querySelector?.("iframe")?.contentDocument;
  if (!doc?.body || !widget) return false;

  const { priceMarks, timeMarks } = readLibraryAxisMarks(widget);
  const dpr = Math.max(1, doc.defaultView?.devicePixelRatio || 1);
  const tickColor = darkMode ? "#E0E3EB" : "#131722";
  const tickLen = isMobile ? 6 : 8;

  const priceAxis =
    Array.from(doc.querySelectorAll(".price-axis")).find((ax) => {
      const r = ax.getBoundingClientRect();
      return r.width > 8 && r.height > 30;
    }) || doc.querySelector(".price-axis");
  const timeAxis = doc.querySelector(".time-axis");

  if (priceAxis) {
    const layer = ensureAxisTickLayer(priceAxis, "fx-mt5-price-ticks", doc);
    if (layer) {
      const { w, h } = sizeTickCanvas(layer, priceAxis, dpr);
      const ctx = layer.getContext("2d");
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        const labeled = (priceMarks || []).filter(markHasNumber);
        if (labeled.length) {
          const len = Math.min(tickLen, Math.max(4, w - 2));
          ctx.strokeStyle = tickColor;
          ctx.lineWidth = 1.25;
          labeled.forEach((m) => {
            const y = Number(m.coord);
            // Keep clear of the bottom corner so ticks don't run into the gear cell
            if (!Number.isFinite(y) || y < 3 || y > h - 8) return;
            const yy = Math.round(y) + 0.5;
            ctx.beginPath();
            ctx.moveTo(0, yy);
            ctx.lineTo(len, yy);
            ctx.stroke();
          });
        }
      }
    }
  }

  if (timeAxis) {
    const layer = ensureAxisTickLayer(timeAxis, "fx-mt5-time-ticks", doc);
    if (layer) {
      const { w, h } = sizeTickCanvas(layer, timeAxis, dpr);
      const ctx = layer.getContext("2d");
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        const labeled = (timeMarks || []).filter(markHasNumber);
        if (labeled.length) {
          const len = Math.min(tickLen, Math.max(4, h - 2));
          ctx.strokeStyle = tickColor;
          ctx.lineWidth = 1.25;
          labeled.forEach((m) => {
            const x = Number(m.coord);
            // Keep clear of the right corner (gear / price-axis join)
            if (!Number.isFinite(x) || x < 3 || x > w - 12) return;
            const xx = Math.round(x) + 0.5;
            ctx.beginPath();
            ctx.moveTo(xx, 0);
            ctx.lineTo(xx, len);
            ctx.stroke();
          });
        }
      }
    }
  }

  return true;
}

export default function TvChartWidget({
  symbol,
  selectedRow,
  darkMode = false,
  viewOnly = false,
  isMobile = false,
}) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const chartReadyRef = useRef(false);
  const [boxReady, setBoxReady] = useState(false);
  const [hideBootStrip, setHideBootStrip] = useState(false);

  const marketWatchList = useSelector((s) => s.marketWatch?.marketWatchList);
  const scriptIndexList = useSelector((s) => s.marketWatch?.scriptIndexList);

  const symbolKey = useMemo(
    () => getSymbolFromProps(symbol, selectedRow),
    [symbol, selectedRow]
  );

  const segmentId = useMemo(() => {
    const raw =
      selectedRow?.segmentId ??
      selectedRow?.SegmentId ??
      selectedRow?.segment?.value ??
      selectedRow?.segment;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [selectedRow]);

  const symbolKeyRef = useRef(symbolKey);
  symbolKeyRef.current = symbolKey;

  const segmentIdRef = useRef(segmentId);
  segmentIdRef.current = segmentId;

  const datafeed = useMemo(
    () =>
      createDatafeed(
        () => symbolKeyRef.current,
        () => segmentIdRef.current
      ),
    []
  );

  const checkBox = useCallback(() => {
    const el = containerRef.current;
    if (!el) return false;
    const r = el.getBoundingClientRect();
    if (r.width > 50 && r.height > 50) {
      setBoxReady(true);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (boxReady) return undefined;
    if (checkBox()) return undefined;
    const ro = new ResizeObserver(() => checkBox());
    if (containerRef.current) ro.observe(containerRef.current);
    const timers = [50, 200, 600, 1200].map((ms) => setTimeout(checkBox, ms));
    return () => {
      ro.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [boxReady, checkBox]);

  const hasSymbol = Boolean(symbolKey);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !hasSymbol || !boxReady || !window.TradingView?.widget) return;

    setHideBootStrip(false);
    chartReadyRef.current = false;

    const bootBg = darkMode ? "#131722" : "#ffffff";
    const w = new window.TradingView.widget({
      container: el,
      datafeed,
      symbol: symbolKeyRef.current,
      interval: "1",
      library_path: `${process.env.PUBLIC_URL || ""}/charting_library/`,
      locale: "en",
      fullscreen: false,
      autosize: true,
      theme: darkMode ? "dark" : "light",
      timezone: "Asia/Kolkata",
      // Intervals / indicators live in our top bar; chart type is left of New Order.
      disabled_features: [
        "use_localstorage_for_settings",
        "header_widget",
        "timeframes_toolbar",
        "insert_indicator_dialog_shortcut",
        "popup_hints",
      ],
      // Keep left_toolbar mounted for selectLineTool(); strip is removed in the
      // iframe after ready. Start hidden so we fight 5px not 52px.
      enabled_features: ["hide_left_toolbar_by_default", "show_object_tree"],
      custom_css_url: `${process.env.PUBLIC_URL || ""}/charting_library/fx-chart-custom.css?v=mt5-legend-2`,
      // Keep time/date axis readable on mobile (web already has enough height).
      time_scale: {
        min_bar_spacing: isMobile ? 2 : 2,
      },
      overrides: {
        // Candles = TradingView ChartStyle 1 — default when prefs missing/invalid
        "mainSeriesProperties.style":
          chartStyleFromPref(loadChartPreferences().defaultChartType) || 1,
        ...getColorTemplateOverrides(loadChartPreferences().colorTemplateId),
        "mainSeriesProperties.candleStyle.drawWick": true,
        "mainSeriesProperties.candleStyle.drawBorder": true,
        // MT5-like: grid from price (left of Y) + from time (above X)
        ...mt5AxisGridOverrides(darkMode, isMobile),
      },
      loading_screen: {
        backgroundColor: bootBg,
        foregroundColor: bootBg,
      },
    });

    widgetRef.current = w;

    let cancelled = false;
    let poll = null;
    let earlyPoll = null;
    let iframeMo = null;
    let sizeRo = null;

    // Inject strip-hiding CSS the moment the iframe document exists — do not wait
    // for onChartReady, or the 5px column flashes on refresh.
    const tryEarlyCss = () => {
      if (cancelled) return;
      if (injectStripCssEarly(el) && earlyPoll) {
        clearInterval(earlyPoll);
        earlyPoll = null;
      }
    };
    earlyPoll = setInterval(tryEarlyCss, 50);
    try {
      iframeMo = new MutationObserver(tryEarlyCss);
      iframeMo.observe(el, { childList: true, subtree: true });
    } catch {}
    tryEarlyCss();

    const refreshLayout = () => {
      if (cancelled) return;
      const stripGone = removeNativeToolbarStrip(el);
      hideChartPopupHints(el);
      ensureTimeAxisVisible(el);
      try { w.resize?.(); } catch {}
      ensurePaneGridClip(el, darkMode);
      ensureMt5AxisTicks(el, w, darkMode, isMobile);
      if (stripGone || chartReadyRef.current) setHideBootStrip(true);
      requestAnimationFrame(() => {
        if (cancelled) return;
        ensurePaneGridClip(el, darkMode);
        ensureMt5AxisTicks(el, w, darkMode, isMobile);
      });
    };

    let resizeTimer = null;
    const scheduleRefresh = () => {
      if (cancelled) return;
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resizeTimer = null;
        refreshLayout();
      }, 80);
    };

    const clipPoll = setInterval(() => {
      if (cancelled) return;
      ensurePaneGridClip(el, darkMode);
      ensureMt5AxisTicks(el, w, darkMode, isMobile);
      hideChartPopupHints(el);
    }, 400);

    try {
      w.onChartReady(() => {
        setChartWidget(w);
        chartReadyRef.current = true;
        applyChartSymbol(w, symbolKeyRef.current);

        try {
          const chart = w.activeChart?.() || w.chart?.();
          // Default chart type is Candles (ChartStyle = 1); prefs coerce unknown → candle.
          const styleId = chartStyleFromPref(loadChartPreferences().defaultChartType) || 1;
          if (chart?.setChartType && chart.chartType?.() !== styleId) {
            chart.setChartType(styleId);
          }
          if (chart?.getCheckableActionState?.("drawingToolbarAction")) {
            chart.executeActionById("drawingToolbarAction");
          }
          // Re-apply saved template colors after ready (widget can reset series colors).
          w.applyOverrides?.({
            ...getColorTemplateOverrides(loadChartPreferences().colorTemplateId),
            ...mt5AxisGridOverrides(darkMode, isMobile),
          });
          // Nudge bar spacing so time tick labels recompute on narrow screens.
          // Start more zoomed-out so more candles fit in view
          try {
            const ts = chart?.getTimeScale?.();
            if (ts?.setBarSpacing) {
              ts.setBarSpacing(isMobile ? 2.5 : 2.5);
            }
          } catch {}
          try {
            setLegendResolution(chart?.resolution?.() || "1");
            chart?.onIntervalChanged?.()?.subscribe?.(null, (interval) => {
              if (cancelled) return;
              setLegendResolution(interval);
              applyChartSymbol(w, symbolKeyRef.current, true);
            });
          } catch {}
          try {
            chart?.onVisibleRangeChanged?.()?.subscribe?.(null, () => {
              if (!cancelled) ensureMt5AxisTicks(el, w, darkMode, isMobile);
            });
          } catch {}
          try {
            chart?.getTimeScale?.()?.barSpacingChanged?.()?.subscribe?.(null, () => {
              if (!cancelled) ensureMt5AxisTicks(el, w, darkMode, isMobile);
            });
          } catch {}
        } catch {}

        refreshLayout();
        setHideBootStrip(true);
        let n = 0;
        poll = setInterval(() => {
          refreshLayout();
          n += 1;
          if (n >= 24) {
            clearInterval(poll);
            poll = null;
          }
        }, 200);

        // Mobile layout settles after footer/header mount — keep resizing with the box.
        try {
          sizeRo = new ResizeObserver(() => scheduleRefresh());
          sizeRo.observe(el);
        } catch {}
      });
    } catch {}

    /** Live-apply Color Templates menu selection to candle/bar colors (no chart recreate). */
    const onChartPrefs = (e) => {
      if (cancelled) return;
      const id =
        e?.detail?.colorTemplateId ?? loadChartPreferences().colorTemplateId;
      try {
        w.applyOverrides?.(getColorTemplateOverrides(id));
      } catch {}
    };
    window.addEventListener(CHART_PREF_EVENT, onChartPrefs);

    return () => {
      cancelled = true;
      chartReadyRef.current = false;
      window.removeEventListener(CHART_PREF_EVENT, onChartPrefs);
      if (poll) clearInterval(poll);
      if (earlyPoll) clearInterval(earlyPoll);
      if (clipPoll) clearInterval(clipPoll);
      if (iframeMo) iframeMo.disconnect();
      if (sizeRo) sizeRo.disconnect();
      if (resizeTimer) clearTimeout(resizeTimer);
      if (widgetRef.current === w) {
        setChartWidget(null);
        try { w.remove(); } catch {}
        widgetRef.current = null;
      }
    };
  }, [hasSymbol, darkMode, viewOnly, isMobile, datafeed, boxReady]);

  useEffect(() => {
    if (!symbolKey || !chartReadyRef.current) return;
    applyChartSymbol(widgetRef.current, symbolKey);
  }, [symbolKey]);

  useEffect(() => {
    if (!symbolKey) return;
    const key = symbolKey.toUpperCase();
    const rows = [...collectRows(marketWatchList), ...collectRows(scriptIndexList)];
    const fromWatch = rows.find((r) => {
      const s = String(r?.script ?? r?.displayName ?? r?.symbol ?? "").toUpperCase();
      return s === key;
    });
    // Prefer Market Watch live quotes; History rows often lack LTP/bid/ask.
    const row =
      fromWatch ||
      (selectedRow && typeof selectedRow === "object" ? selectedRow : null);
    if (!row) return;
    const ltp = Number(row.lastTradedPrice ?? row.ltp);
    if (!Number.isFinite(ltp) || ltp <= 0) return;
    datafeed.pushLiveTick(symbolKey, ltp, Number(row.volume ?? row.Volume) || 0);
  }, [marketWatchList, scriptIndexList, symbolKey, selectedRow, datafeed]);

  return (
    <div
      ref={containerRef}
      className={`tv-charting-widget${hideBootStrip ? "" : " tv-charting-widget--booting"}`}
    />
  );
}
